// FE-SCRM-052 — Opportunities list view.
//
// Peer surface to the Renewal Board, NOT a sub-tab of Sales CRM —
// the spec is explicit on that distinction (line 2143). The Renewal
// Board lays the pipeline across the 12-month renewal window by
// stage; this view flattens it for "show me every BOR takeover
// regardless of renewal month" / "show me everything I lost this
// quarter" / "show me every opportunity my colleague owns".
//
// Reached from:
// - the Renewal Board overflow menu ("View all as list", FE-SCRM-008);
// - Prospect detail's Opportunities-list section "View all"
//   (FE-SCRM-050 future);
// - the global search modal (FE-SCRM-054) when the user picks an
//   opportunity entity match.
//
// URL is canonical: filters / sort / search / pagination / archived
// all round-trip through ?filter=…&ordering=…&q=…&page=…&page_size=
// &archived=on so deep links survive reloads.

import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  CrmSearchInput,
  IncludeArchivedToggle,
  Pagination,
  readIncludeArchivedFromUrl,
  readPaginationFromUrl,
  readSearchFromUrl,
} from "@/features/sales/components";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";
import { AddOpportunityModal } from "@/features/sales/AddOpportunity";

import { FilterBar } from "./FilterBar";
import { OpportunityTable } from "./Table";
import { useOpportunityListQuery } from "./hooks";
import {
  buildApiQuery,
  hasActiveFilters,
  readFiltersFromUrl,
  readOrderingFromUrl,
} from "./urlState";
import { EMPTY_FILTERS } from "./types";
import { writeFiltersToParams } from "./urlState";


export function OpportunitiesListPage() {
  const [params, setParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(false);

  // FE-SCRM-038: "Clear filters" empty-state CTA. Resets filters +
  // search + archived back to the canonical empty shape; pagination
  // resets so the user doesn't land on a page that no longer exists.
  const clearAllFilters = () => {
    const next = writeFiltersToParams(EMPTY_FILTERS, params);
    next.delete("q");
    next.delete("archived");
    next.delete("page");
    setParams(next, { replace: false });
  };

  const filters = useMemo(() => readFiltersFromUrl(params), [params]);
  const ordering = readOrderingFromUrl(params);
  const q = readSearchFromUrl(params);
  const archived = readIncludeArchivedFromUrl(params);
  const { page, pageSize } = readPaginationFromUrl(params);

  const apiQuery = useMemo(
    () => buildApiQuery({
      filters, ordering, q, page, pageSize, archived,
    }),
    [filters, ordering, q, page, pageSize, archived],
  );

  const list = useOpportunityListQuery(apiQuery);

  const isFiltered = hasActiveFilters(filters) || q !== "" || archived;

  return (
    <div
      data-testid="opportunities-list-page"
      className="flex flex-col gap-4"
    >
      <header className="flex items-baseline justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-lg font-semibold tracking-tight text-text-primary">
            Opportunities
          </h1>
          <p className="text-xs text-text-secondary">
            Flat view of every pursuit across renewal months and stages.
            The Renewal Board is the primary navigation; this surface
            is for cross-cutting queries the grid can't express.
          </p>
        </div>
        {/* Mirror of the Renewal Board's "View all as list" overflow
            link (RenewalBoardPanel.tsx:263) so the toggle back to the
            geometric view sits in the same top-right position the
            producer just clicked from. */}
        <div className="flex items-center gap-3">
          <Link
            data-testid="opportunities-back-to-board"
            to="/dashboard/sales-crm?tab=pipeline"
            className={[
              "text-xs font-medium text-text-tertiary underline",
              "hover:text-text-primary",
            ].join(" ")}
          >
            View as Renewal Board
          </Link>
          <button
            type="button"
            data-testid="opportunities-add-cta"
            onClick={() => setAddOpen(true)}
            className={[
              "rounded-md bg-teal px-3 py-1.5 text-sm font-medium",
              "text-text-inverse hover:opacity-90",
            ].join(" ")}
          >
            + Add opportunity
          </button>
        </div>
      </header>

      <AddOpportunityModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />

      <div className="flex flex-wrap items-center gap-3">
        <CrmSearchInput
          placeholder="Search by opportunity or prospect name"
          className="w-72"
        />
        <IncludeArchivedToggle />
      </div>

      <FilterBar />

      {list.isLoading ? (
        <div data-testid="opportunities-loading">
          <DashboardLoadingState variant="card" />
        </div>
      ) : list.isError ? (
        <div data-testid="opportunities-error">
          <DashboardErrorState
            title="Couldn't load opportunities"
            description="Refresh to retry, or check your network connection."
            retry={() => list.refetch()}
          />
        </div>
      ) : list.data && list.data.rows.length > 0 ? (
        <>
          <OpportunityTable rows={list.data.rows} />
          <Pagination total={list.data.total} />
        </>
      ) : (
        <div data-testid="opportunities-empty-state">
          {isFiltered ? (
            <DashboardEmptyState
              title="No Opportunities match your filters."
              description={
                "Closed Won / Closed Lost stages only surface when "
                + "their chips are explicitly on. Clear filters to "
                + "see your full pipeline."
              }
              action={
                <button
                  type="button"
                  data-testid="opportunities-empty-clear-filters-cta"
                  onClick={clearAllFilters}
                  className={[
                    "inline-flex items-center justify-center rounded-md",
                    "bg-teal px-4 py-2 text-sm font-medium",
                    "text-text-inverse hover:opacity-90",
                  ].join(" ")}
                >
                  Clear filters
                </button>
              }
            />
          ) : (
            <DashboardEmptyState
              title="No opportunities yet"
              description={
                "Add your first pursuit to lay it on the Renewal Board. "
                + "Importing prospects via Settings → Import data also "
                + "spawns opportunities for each renewal-month prospect."
              }
            />
          )}
        </div>
      )}
    </div>
  );
}
